import { ButcherHeader } from '@components/ButcherHeader';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import { CustomerDetailsCrumbSteps, errorMessage } from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { OrderType } from '@helpers/Models';
import {
  formatDate,
  handleDetailsView,
  handleNewOrderInitiation,
} from '@services/OrderCreationService';
import { SubscriptionValidation } from '@services/SubscriptionValidation';
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDismissDropdown } from '@services/DropdownService';

export const OrderHistory: React.FC = () => {
  const { state } = useLocation();
  const { id, firstName, lastName, phoneHome, primaryEmail, HuntingLicences } =
    state || {};
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderType[]>([]);
  const { showToastMessage } = useToast();
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false);
  // Remove the 'Details' step to show only Customers > Name > Order History in breadcrumbs
  const OrderBreadcrumbSteps = CustomerDetailsCrumbSteps.filter(
    (step) => step !== 'Details',
  );

  useEffect(() => {
    fetchOrderHistory();

    // Verify the subscription status
    setIsSubscriptionActive(SubscriptionValidation());
  }, []);

  useDismissDropdown({
    refs: Array.from(dropdownRefs.current.values()).map((el) => ({
      current: el,
    })),
    onClose: () => setActiveDropdown(null),
  });

  /**
   * Fetches the full order history for a specific customer.
   *
   * @returns {Promise<void>} A Promise that resolves after the order data is retrieved and state is updated.
   *
   * @throws Will display a toast and rethrow the error if the request fails.
   *
   * @remarks
   * - Sends a POST request to the `CUSTOMER_ORDER_HISTORY` API endpoint with the customer `id`.
   * - Updates the `orders` state with the returned order list.
   * - Relies on `id` being available in the surrounding scope.
   *
   */
  const fetchOrderHistory = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.CUSTOMER_ORDER_HISTORY, {
        customerId: id,
      });
      setOrders(response.data.data.orders);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Prepares and initiates the creation of a new order for a given customer.
   *
   * @remarks
   * This function constructs a `customerDetails` object using data assumed to be
   * available in the current scope (e.g., `id`, `firstName`, `lastName`, etc.)
   * and then calls `handleNewOrderInitiation` with the required parameters.
   *
   * It also relies on `navigate` to potentially redirect to a new route during order initiation.
   */
  const handleCreateNewOrder = (): void => {
    const customerDetails = {
      id: id,
      firstName: firstName,
      lastName: lastName,
      phoneHome: phoneHome,
      primaryEmail: primaryEmail,
      HuntingLicences: HuntingLicences,
    };
    handleNewOrderInitiation(navigate, customerDetails);
  };

  const toggleDropdown = (orderId: number): void => {
    setActiveDropdown((prev) => (prev === orderId ? null : orderId));
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderBreadcrumb
          steps={OrderBreadcrumbSteps}
          activeItem="Order History"
          customerName={`${firstName} ${lastName}`}
          customerId={id}
        />
        <div className="flex items-center justify-between mt-4">
          <div>
            <h2 className="text-lg font-semibold rounded-md ml-2">
              Order History
            </h2>
          </div>
          <div className="flex mr-4">
            <button
              disabled={isSubscriptionActive}
              onClick={handleCreateNewOrder}
              type="button"
              className={`ml-2 px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSubscriptionActive ? 'cursor-not-allowed bg-[#00483480]' : 'bg-primary'} hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2`}
            >
              New Order
            </button>
          </div>
        </div>
        {/* Customer Profile Card */}
        <OrderProfileCard
          customerName={`${firstName} ${lastName}`}
          phoneHome={phoneHome}
        />
        <div className="border-b mt-3 rounded-lg">
          <form>
            <table className="w-full table-fixed rounded-xl">
              <thead className="bg-gray-100">
                <tr className="border-b">
                  <th className="w-1/4 px-4 py-2 text-center font-semibold">
                    Order No.
                  </th>
                  <th className="w-1/4 px-4 py-2 text-center font-semibold">
                    Created On
                  </th>
                  <th className="w-1/4 px-4 py-2 text-center font-semibold">
                    Order Status
                  </th>
                  <th className="w-1/4 px-4 py-2 text-center font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() =>
                        handleDetailsView(
                          order.id,
                          navigate,
                          FrontendRoutes.ORDER_DETAILS,
                        )
                      }
                      className="hover:cursor-pointer"
                    >
                      <td className="px-4 py-2 text-center font-semibold">
                        {order.orderNumber}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap flex justify-center">
                        <OrderStatusBadge
                          type="orderStatus"
                          status={order.status}
                        />
                      </td>
                      <td className="px-4 py-2 text-center relative">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleDropdown(order.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                        {activeDropdown === order.id && (
                          <div
                            className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10"
                            ref={(el) => {
                              if (el) {
                                dropdownRefs.current.set(order.id, el);
                              } else {
                                dropdownRefs.current.delete(order.id);
                              }
                            }}
                          >
                            <ul className="py-2 text-left">
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveDropdown(null);
                                    handleDetailsView(
                                      order.id,
                                      navigate,
                                      FrontendRoutes.ORDER_DETAILS,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View order summary
                                </button>
                              </li>
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveDropdown(null);
                                    handleDetailsView(
                                      order.id,
                                      navigate,
                                      FrontendRoutes.SERVICE_PROCESS_TASK_LIST,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View service processing
                                </button>
                              </li>
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveDropdown(null);
                                    handleDetailsView(
                                      order.id,
                                      navigate,
                                      FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View product processing
                                </button>
                              </li>
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setActiveDropdown(null);
                                    navigate(FrontendRoutes.ALL_INVOICES, {
                                      state: { orderId: order.id },
                                    });
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View invoices
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center p-4">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>
        </div>
      </div>
    </div>
  );
};
